/**
 * Calculate direct legacy maturity scores for a specific year
 * @param {string} legacyYear - The legacy year to calculate for
 * @param {Array} processedItems - Array of processed safeguard items
 * @param {Object} qualityData - Quality data containing CRF mappings
 * @returns {Object} Object containing maturity scores by priority level
 */
async function calculateDirectLegacyMaturity(legacyYear, processedItems, qualityData) {
	console.log('=== CALCULATE DIRECT LEGACY MATURITY START ===');
	console.log('Legacy year:', legacyYear);
	console.log('Processed items count:', processedItems ? processedItems.length : 'undefined');

	// 1. Use the quality data that's already loaded for this assessment
	console.log('Step 1: Using existing quality data for legacy year:', legacyYear);
	try {
		console.log('Quality data CRF.Items count:', qualityData.CRF?.Items?.length);

	// 2. Get the mapping of legacy safeguard IDs to their priority tags
	console.log('Step 2: Getting legacy priority map...');
	const legacyPriorityMap = getLegacyPriorityMap(legacyYear, qualityData);
	console.log('Legacy priority map size:', Object.keys(legacyPriorityMap).length);
	console.log('Sample legacy priority entries:', Object.entries(legacyPriorityMap).slice(0, 5));
	
	// Count priorities by level
	const priorityDistribution = {};
	Object.values(legacyPriorityMap).forEach(priority => {
		const level = window.extractNumericLevel ? window.extractNumericLevel(priority) : null;
		const normalized = level ? `P${level}` : 'Unknown';
		priorityDistribution[normalized] = (priorityDistribution[normalized] || 0) + 1;
	});
	console.log('Legacy priority distribution:', priorityDistribution);
	
	if (Object.keys(legacyPriorityMap).length === 0) {
		// console.warn('No legacy priority map found, returning empty object');
		return {};
	}

		// 3. Create a lookup map from modern safeguard ID to legacy (CCC) safeguard ID
		// console.log('Step 3: Building modern to legacy map...');
		const modernToLegacyMap = {};
		// console.log('Initial modernToLegacyMap:', modernToLegacyMap);

		// 4. Get the modern Combined data structure for mapping
		// console.log('Step 4: Getting Combined data structure...');
		if (!qualityData || !qualityData.Combined || !Array.isArray(qualityData.Combined)) {
			// console.warn('No Combined data found in qualityData');
			return {};
		}
		const combinedData = qualityData.Combined;

	// console.log('Step 2b: Processing combined data items...');
	let processedCount = 0;
	combinedData.forEach((item, index) => {
		const modernId = item.safeguardid.S;
		// console.log(`Processing item ${index + 1}/${combinedData.length}:`, modernId);

		if (item.relatedCSItems && Array.isArray(item.relatedCSItems)) {
			// console.log(`  Item ${index + 1} has ${item.relatedCSItems.length} related items`);

			// Find the mapping to the specific legacy year requested
			const legacyMappingItem = item.relatedCSItems.find(related => {
				const isCRFS = related.standardname && (related.standardname.S === 'Cybersecurity Risk Foundation - Safeguards (CRF-S)' || related.standardname.S === 'Cybersecurity Risk Foundation - Safeguards - Core Edition (CRF-S)');
				const itemYear = related.standardversion ? related.standardversion.S.trim() : null;
				const isCorrectYear = itemYear === legacyYear;
				// console.log(`    Checking related item: standard=${related.standardname?.S}, version=${itemYear}, isCRFS=${isCRFS}, isCorrectYear=${isCorrectYear}`);
				return isCRFS && isCorrectYear;
			});

			if (legacyMappingItem) {
				// console.log(`  Found legacy mapping for item ${index + 1}`);
				if (legacyMappingItem.controlid && legacyMappingItem.controlid.S) {
					const legacyIds = legacyMappingItem.controlid.S.trim().split(/\s*\\n\s*|\s*\n\s*/).filter(id => id);
					// console.log(`  Legacy IDs for ${modernId}:`, legacyIds);
					if (legacyIds.length > 0) {
						modernToLegacyMap[modernId] = legacyIds;
						// console.log(`  Added ${legacyIds.length} legacy mappings for ${modernId}`);
					} else {
						// console.log(`  No valid legacy IDs found for ${modernId}`);
					}
				} else {
					// console.log(`  Legacy mapping item missing controlid for ${modernId}`);
				}
			} else {
				// console.log(`  No legacy mapping found for ${modernId} in year ${legacyYear}`);
			}
		} else {
			// console.log(`  Item ${index + 1} has no relatedCSItems`);
		}
		processedCount++;
	});
	// console.log(`Processed ${processedCount} combined data items`);

	// console.log('Step 2c: Modern to legacy mapping complete');
	// console.log('Legacy priority map keys:', Object.keys(legacyPriorityMap));
	// console.log('Modern to legacy map size:', Object.keys(modernToLegacyMap).length);
	// console.log('Sample modernToLegacyMap entries:', Object.keys(modernToLegacyMap).slice(0, 3).map(key => ({ [key]: modernToLegacyMap[key] })));

	// Build a map of legacy priorities to all the modern safeguards that fall under them.
	console.log('Step 3: Building modern safeguards by legacy priority...');
	console.log('Modern to legacy map has', Object.keys(modernToLegacyMap).length, 'entries');
	
	// Debug: Quick check - do any modern safeguards map to legacy IDs with priorities?
	let mappingsWithPriorities = 0;
	let mappingsWithoutPriorities = 0;
	Object.entries(modernToLegacyMap).forEach(([modern, legacyIds]) => {
		const hasPriority = legacyIds.some(id => legacyPriorityMap[id.trim()]);
		if (hasPriority) mappingsWithPriorities++;
		else mappingsWithoutPriorities++;
	});
	console.log('Modern mappings check:', { withPriorities: mappingsWithPriorities, withoutPriorities: mappingsWithoutPriorities });
	
	const modernSafeguardsByLegacyPriority = {
		"P1": new Set(), "P2": new Set(), "P3": new Set(), "P4": new Set(), "P5": new Set()
	};

	Object.keys(modernToLegacyMap).forEach(modernId => {
		const legacyIds = modernToLegacyMap[modernId];
		// console.log(`  Processing modern ID ${modernId} with ${legacyIds.length} legacy IDs:`, legacyIds);

		legacyIds.forEach(legacyId => {
			const trimmedLegacyId = legacyId.trim();
			// console.log(`    Processing legacy ID: "${trimmedLegacyId}"`);

			// First, check if this legacy ID is directly in the priority map (for individual safeguards)
			if (legacyPriorityMap.hasOwnProperty(trimmedLegacyId)) {
				const priority = legacyPriorityMap[trimmedLegacyId];
				// console.log(`    Found direct mapping: "${trimmedLegacyId}" -> "${priority}"`);

				// Normalize priority levels to P1-P5 format
				const normalizedPriority = normalizePriority(priority);
				// console.log(`    Normalized priority "${priority}" to "${normalizedPriority}"`);

				if (normalizedPriority && modernSafeguardsByLegacyPriority[normalizedPriority]) {
					modernSafeguardsByLegacyPriority[normalizedPriority].add(modernId);
					// console.log(`    Added ${modernId} to priority ${normalizedPriority}`);
				} else {
					// console.warn(`    Invalid normalized priority ${normalizedPriority} for legacy ID ${trimmedLegacyId}`);
				}
			} else {
				// console.log(`    "${trimmedLegacyId}" not found as individual safeguard`);

				// This might be a CRF category ID - check if it exists in CRF.Items as a safeguard ID
				// CRF categories are often named like THR-06, CM-16, etc. and may not be in the priority map
				// But they might represent collections of safeguards that we need to handle differently

				// This is a CRF category ID that doesn't exist in the priority map
				// CRF category IDs like PM-02, NSC-01, etc. represent CRF control categories
				// These need to be resolved through CCC mappings

				// console.log(`    "${trimmedLegacyId}" is a CRF category ID not found in priority map`);

				// For CRF category IDs, we need to look at the CCC mapping for this modern safeguard
				// The CRF category ID should map to a CCC category ID

				// Find the CCC mapping for this modern safeguard from the Combined data
				const cccMappingItem = findCCCMappingForModernId(modernId, qualityData);
				if (cccMappingItem && cccMappingItem.cccmapping) {
					// Extract the CCC category ID from the mapping (handle different data types)
					let cccCategoryId = null;
					if (typeof cccMappingItem.cccmapping === 'string') {
						cccCategoryId = cccMappingItem.cccmapping.trim();
					} else if (cccMappingItem.cccmapping.S) {
						// DynamoDB format
						cccCategoryId = cccMappingItem.cccmapping.S.trim();
					} else if (Array.isArray(cccMappingItem.cccmapping) && cccMappingItem.cccmapping.length > 0) {
						// Array format - take first item
						cccCategoryId = cccMappingItem.cccmapping[0].trim();
					} else if (typeof cccMappingItem.cccmapping === 'object' && Object.keys(cccMappingItem.cccmapping).length > 0) {
						// Object format - take first key
						cccCategoryId = Object.keys(cccMappingItem.cccmapping)[0];
					}

					if (cccCategoryId) {
						// console.log(`    Found CCC mapping for ${modernId}: ${cccCategoryId}`);

						// Now look up the priority of this CCC category ID
						if (legacyPriorityMap.hasOwnProperty(cccCategoryId)) {
							const cccPriority = legacyPriorityMap[cccCategoryId];
							// console.log(`    CCC category "${cccCategoryId}" has priority "${cccPriority}"`);

							const normalizedPriority = normalizePriority(cccPriority);
							if (normalizedPriority && modernSafeguardsByLegacyPriority[normalizedPriority]) {
								modernSafeguardsByLegacyPriority[normalizedPriority].add(modernId);
								// console.log(`    Added ${modernId} to priority ${normalizedPriority} via CCC mapping`);
							}
						} else {
							// console.warn(`    CCC category "${cccCategoryId}" not found in priority map`);
							// console.log(`    Available priority map keys:`, Object.keys(legacyPriorityMap).slice(0, 10));
						}
					} else {
						// console.warn(`    Could not extract CCC category ID from mapping for ${modernId}`);
					}
				} else {
					// console.warn(`    No CCC mapping found for ${modernId}`);
				}
			}
		});
	});

	const safeguardsByPriority = Object.fromEntries(
		Object.entries(modernSafeguardsByLegacyPriority).map(([k, v]) => [k, v.size])
	);
	console.log('Modern safeguards by priority after mapping:', safeguardsByPriority);

	// 3. Aggregate scores for the numerator
	console.log('Step 4: Aggregating scores from processed items...');
	const levelScores = {
		"P1": { totalScore: 0, safeguardSet: new Set() },
		"P2": { totalScore: 0, safeguardSet: new Set() },
		"P3": { totalScore: 0, safeguardSet: new Set() },
		"P4": { totalScore: 0, safeguardSet: new Set() }
	};
	console.log('Initial level scores:', Object.fromEntries(Object.entries(levelScores).map(([k, v]) => [k, { totalScore: v.totalScore, count: v.safeguardSet.size }])));

	let processedItemsCount = 0;
	let itemsWithScoresCount = 0;
	let itemsWithLegacyMappingCount = 0;
	let scoresByPriority = { P1: 0, P2: 0, P3: 0, P4: 0, P5: 0 };
	
	processedItems.forEach(item => {
		processedItemsCount++;
		const modernId = item.safeguardid.S;
		const maturityScore = item.maturityscore;

		if (item.maturityscore !== null && item.maturityscore !== undefined) { // Only consider answered, applicable items
			itemsWithScoresCount++;
			const legacyIds = modernToLegacyMap[modernId];

			if (legacyIds && legacyIds.length > 0) {
				itemsWithLegacyMappingCount++;
				legacyIds.forEach(legacyId => {
					const rawLegacyPriority = legacyPriorityMap[legacyId.trim()];

					if (rawLegacyPriority) {
						// Normalize the priority to P1-P5 format
						const legacyPriority = normalizePriority(rawLegacyPriority);

						if (levelScores[legacyPriority]) {
							if (!levelScores[legacyPriority].safeguardSet.has(modernId)) {
								levelScores[legacyPriority].totalScore += item.maturityscore;
								levelScores[legacyPriority].safeguardSet.add(modernId);
								scoresByPriority[legacyPriority]++;
							}
						}
					}
				});
			}
		}
	});

	console.log('Processed items with scores:', itemsWithScoresCount, 'out of', processedItemsCount);
	console.log('Items with legacy mapping:', itemsWithLegacyMappingCount);
	console.log('Items scored per priority:', scoresByPriority);
	console.log('Final level scores after aggregation:', Object.fromEntries(Object.entries(levelScores).map(([k, v]) => [k, { totalScore: v.totalScore, count: v.safeguardSet.size }])));

	// Create a set of all modern safeguards marked as "Not Applicable"
	// console.log('Step 5: Creating NA items set...');
	const naModernItems = new Set();
	let naCount = 0;
	processedItems.forEach(item => {
		if (item.policystatus && parseInt(item.policystatus.N, 10) === 1) {
			naModernItems.add(item.safeguardid.S);
			naCount++;
		}
	});
	// console.log(`Found ${naCount} Not Applicable items out of ${processedItems.length} total`);

	// 4. Calculate the final average for each level using the correct denominator
	console.log('Step 6: Calculating final averages...');
	console.log('Modern safeguards distribution by priority:', {
		P1: modernSafeguardsByLegacyPriority["P1"].size,
		P2: modernSafeguardsByLegacyPriority["P2"].size,
		P3: modernSafeguardsByLegacyPriority["P3"].size,
		P4: modernSafeguardsByLegacyPriority["P4"].size,
		P5: modernSafeguardsByLegacyPriority["P5"].size
	});
	const finalAverages = {};
	Object.keys(levelScores).forEach(level => {
		console.log(`  Calculating ${level} level...`);
		const totalScore = levelScores[level].totalScore; // Numerator
		const potentialModernIdsForLevel = modernSafeguardsByLegacyPriority[level] || new Set();

		console.log(`    ${level} - Total score: ${totalScore}, Potential safeguards: ${potentialModernIdsForLevel.size}`);

		let naCountForLevel = 0;
		potentialModernIdsForLevel.forEach(modernId => {
			if (naModernItems.has(modernId)) {
				naCountForLevel++;
			}
		});

		console.log(`    ${level} - NA count: ${naCountForLevel}`);
		const denominator = potentialModernIdsForLevel.size - naCountForLevel;
		console.log(`    ${level} - Denominator (total - NA): ${denominator}`);

		if (denominator > 0) {
			const averageScore = totalScore / denominator;
			const scaledScore = averageScore * 5; // Scale to 5-point (return as number, not string)
			finalAverages[level] = scaledScore;
			console.log(`    ${level} - Final average: ${scaledScore.toFixed(2)} (raw: ${averageScore}, unrounded: ${scaledScore})`);
		} else {
			finalAverages[level] = 0; // Return as number for consistency
			console.log(`    ${level} - No valid items, setting to 0`);
		}
	});

		console.log('=== CALCULATE DIRECT LEGACY MATURITY END ===');
		console.log('Final legacy averages for year', legacyYear, ':', finalAverages);
		return finalAverages;

	} catch (error) {
		console.error('Error in calculateDirectLegacyMaturity:', error);
		return {};
	}
}

/**
 * Fetch quality data for a specific year
 * @param {string} year - The year to fetch data for
 * @returns {Promise<Object>} Promise that resolves with quality data or rejects with error
 */
function fetchQualityDataForYear(year) {
	console.log('legacyMaturityCalculations: fetchQualityDataForYear called with year:', year);
	return new Promise((resolve, reject) => {
		console.log(`[fetchQualityDataForYear] Fetching quality data for year: ${year}`);
		
		// Use localized ajax_url if available, otherwise fall back to default path
		const ajaxUrl = (typeof controlsData !== 'undefined' && controlsData.ajax_url) 
			? controlsData.ajax_url 
			: '/wp-admin/admin-ajax.php';
		
		console.log(`[fetchQualityDataForYear] Using AJAX URL: ${ajaxUrl}`);
		
		jQuery.ajax({
			url: ajaxUrl,
			method: 'POST',
			data: {
				action: 'get_quality_data_by_year',
				year: year
			},
			dataType: 'json',
			success: function (data) {
				console.log(`[fetchQualityDataForYear] Success response for year ${year}:`, {
					hasCRF: !!data.CRF,
					hasCRFItems: !!(data.CRF && data.CRF.Items),
					hasCombined: !!data.Combined,
					crfItemsCount: data.CRF?.Items?.length || 0,
					combinedCount: Array.isArray(data.Combined) ? data.Combined.length : 'not array'
				});
				
				if (data.CRF && data.CRF.Items && data.Combined) {
					console.log(`[fetchQualityDataForYear] Data structure valid for year ${year}`);
					resolve(data);
				} else {
					console.error(`[fetchQualityDataForYear] Data structure invalid for year ${year}:`, data);
					reject(`Data is missing expected structure for year ${year}`);
				}
			},
			error: function (xhr, status, error) {
				console.error(`[fetchQualityDataForYear] AJAX error for year ${year}:`, {
					status: xhr.status,
					statusText: xhr.statusText,
					responseText: xhr.responseText,
					error: error
				});
				reject('Error fetching data: ' + error);
			}
		});
	});
}

/**
 * Get legacy priority map for a specific year
 * @param {string} legacyYear - The legacy year to get priority map for
 * @param {Object} qualityData - Quality data containing CRF mappings
 * @returns {Object} Object mapping legacy safeguard IDs to priority tags
 */
function getLegacyPriorityMap(legacyYear, qualityData) {
	// console.log('=== GET LEGACY PRIORITY MAP START ===');
	// console.log('Legacy year requested:', legacyYear);
	// console.log('Quality data provided:', !!qualityData);

	const legacyPriorityMap = {};

	if (!qualityData) {
		// console.warn('No qualityData provided to getLegacyPriorityMap');
		// console.log('=== GET LEGACY PRIORITY MAP END (empty) ===');
		return legacyPriorityMap;
	}

	// console.log('Quality data keys:', Object.keys(qualityData));

	if (!qualityData.CRF) {
		// console.warn('No CRF data in qualityData');
		// console.log('Available keys in qualityData:', Object.keys(qualityData));
		// console.log('=== GET LEGACY PRIORITY MAP END (no CRF) ===');
		return legacyPriorityMap;
	}

	// console.log('CRF structure keys:', Object.keys(qualityData.CRF));

	// Since we're using individual year files, we need to build the priority map differently
	// The CRF.Items structure contains all safeguards from all years, but we need to filter
	// based on the Combined data mappings to get the correct priorities for the legacy year

	// Build priority map from Combined data by finding CRF-S mappings for the legacy year
	if (!qualityData.Combined || !Array.isArray(qualityData.Combined)) {
		// console.warn('No Combined data found for legacy priority mapping');
		return legacyPriorityMap;
	}

	// Find all CRF-S items for the legacy year from Combined data
	let foundMappings = 0;
	let prioritiesFromMapping = 0;
	let prioritiesFromModern = 0;
	let noPriorityFound = 0;
	
	qualityData.Combined.forEach(item => {
		if (item.relatedCSItems && Array.isArray(item.relatedCSItems)) {
			const legacyMapping = item.relatedCSItems.find(related =>
				(related.standardname?.S === 'Cybersecurity Risk Foundation - Safeguards (CRF-S)' || related.standardname?.S === 'Cybersecurity Risk Foundation - Safeguards - Core Edition (CRF-S)') &&
				related.standardversion?.S === legacyYear
			);

			if (legacyMapping && legacyMapping.controlid?.S) {
				foundMappings++;
				// This item maps to CRF-S for the legacy year
				// The priority should be in the legacyMapping object itself (controlprioritytag)
				// OR we can get it from the modern item's priority since they map to each other
				
				const legacyIds = legacyMapping.controlid.S.trim().split(/\s*\\n\s*|\s*\n\s*/).filter(id => id);
				
				legacyIds.forEach(legacyId => {
					const trimmedId = legacyId.trim();
					
					// First, try to get priority from the legacy mapping itself
					if (legacyMapping.controlprioritytag?.S) {
						legacyPriorityMap[trimmedId] = legacyMapping.controlprioritytag.S;
						prioritiesFromMapping++;
					}
					// If not in mapping, try to get from the modern item's priority
					else if (item.safeguardprioritytag?.S) {
						legacyPriorityMap[trimmedId] = item.safeguardprioritytag.S;
						prioritiesFromModern++;
					} else {
						noPriorityFound++;
					}
				});
			}
		}
	});
	
	console.log('Priority map building:', {
		foundMappings,
		prioritiesFromMapping,
		prioritiesFromModern,
		noPriorityFound,
		totalInMap: Object.keys(legacyPriorityMap).length
	});

	// If we couldn't find any CRF-S mappings for the legacy year, try a fallback approach
	if (Object.keys(legacyPriorityMap).length === 0) {
		// console.warn(`No CRF-S mappings found for legacy year ${legacyYear} in Combined data`);

		// Fallback: Try to use CRF.Items directly and filter based on available mappings
		if (qualityData.CRF?.Items) {
			// console.log(`Using fallback: CRF.Items with ${qualityData.CRF.Items.length} items`);
			qualityData.CRF.Items.forEach(item => {
				if (item.safeguardid?.S && item.safeguardprioritytag?.S) {
					legacyPriorityMap[item.safeguardid.S] = item.safeguardprioritytag.S;
				}
			});
		}
	}

	// console.log(`Built legacy priority map with ${Object.keys(legacyPriorityMap).length} entries for year ${legacyYear}`);
	// console.log('All legacy priority map entries:', Object.entries(legacyPriorityMap).map(([key, value]) => ({ [key]: value })));
	return legacyPriorityMap;
}


/**
 * Normalize a priority string to P1-P5 format
 * Uses the extractNumericLevel utility to extract the numeric level
 * @param {string} priority - The priority string (e.g., "Controlled (Level 4)", "P2")
 * @returns {string} Normalized priority (P1-P5)
 */
function normalizePriority(priority) {
	// Use the utility function (loaded via extractNumericLevel.js)
	const level = window.extractNumericLevel ? window.extractNumericLevel(priority) : null;
	
	if (level !== null && level >= 1 && level <= 5) {
		return `P${level}`;
	}
	
	console.warn(`Unknown priority format: "${priority}", defaulting to P1`);
	return 'P1'; // Default fallback
}

/**
 * Get the numeric level of a priority (P1=1, P2=2, etc.)
 * @param {string} priority - The priority string (P1-P5)
 * @returns {number} The numeric level
 */
function getPriorityLevel(priority) {
	return parseInt(priority.substring(1)); // Extract number from "P1", "P2", etc.
}

/**
 * Find CCC mapping for a modern safeguard ID
 * @param {string} modernId - The modern safeguard ID to find CCC mapping for
 * @param {Object} qualityData - Quality data containing Combined structure
 * @returns {Object|null} CCC mapping item or null if not found
 */
function findCCCMappingForModernId(modernId, qualityData) {
	if (!qualityData.Combined || !Array.isArray(qualityData.Combined)) {
		return null;
	}

	// Find the item in Combined data that matches the modern safeguard ID
	const combinedItem = qualityData.Combined.find(item => item.safeguardid.S === modernId);
	if (!combinedItem) {
		return null;
	}

	// Find the CCC mapping for this item
	const cccMappingItem = combinedItem.relatedCSItems?.find(related => {
		if (!related.cccmapping) return false;

		// Handle different types of cccmapping values
		if (typeof related.cccmapping === 'string') {
			return related.cccmapping.trim();
		} else if (related.cccmapping.S) {
			// DynamoDB format
			return related.cccmapping.S.trim();
		} else if (Array.isArray(related.cccmapping)) {
			// Array format
			return related.cccmapping.length > 0;
		} else if (typeof related.cccmapping === 'object') {
			// Object format
			return Object.keys(related.cccmapping).length > 0;
		}

		return false;
	});

	return cccMappingItem || null;
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
	module.exports = { calculateDirectLegacyMaturity, getLegacyPriorityMap, findCCCMappingForModernId, fetchQualityDataForYear, normalizePriority, getPriorityLevel };
} else if (typeof window !== 'undefined') {
	window.calculateDirectLegacyMaturity = calculateDirectLegacyMaturity;
	window.getLegacyPriorityMap = getLegacyPriorityMap;
	window.findCCCMappingForModernId = findCCCMappingForModernId;
	window.fetchQualityDataForYear = fetchQualityDataForYear;
	window.normalizePriority = normalizePriority;
	window.getPriorityLevel = getPriorityLevel;
}
